// Scoped to both gift card product pages. Cart submission remains native.
export function resolveAmount(value, variants, currency = 'EUR') {
  if (currency !== 'EUR') return { error: 'Kies EUR als valuta om deze giftcard te bestellen.' };
  if (!/^[0-9]+$/.test(value)) return { error: 'Vul een bedrag in hele euro’s in, zonder decimalen.' };
  const amount = Number(value);
  if (amount < 25 || amount > 500) return { error: 'Vul een bedrag in tussen €25 en €500.' };
  const matches = variants.filter(variant => {
    const denomination = String(variant.option1).replace(/€/g, '').trim().replace(',', '.');
    return /^\d+(?:\.0{1,2})?$/.test(denomination) && Number(denomination) === amount && variant.price === amount * 100;
  });
  if (matches.length !== 1) return { error: 'Dit bedrag is momenteel niet beschikbaar. Kies een ander bedrag.' };
  if (!matches[0].available) return { error: 'Dit bedrag is momenteel uitverkocht. Kies een ander bedrag.' };
  return { variant: matches[0] };
}

class GiftcardAmount extends HTMLElement {
  connectedCallback() {
    this.lifecycle = new AbortController();
    const lifecycle = this.lifecycle;
    Promise.all([customElements.whenDefined('product-form'), customElements.whenDefined('product-rerender')]).then(() => {
      if (!this.isConnected || lifecycle.signal.aborted) return;
      this.form = document.getElementById(this.getAttribute('form-id'));
      this.input = this.querySelector('input');
      this.error = this.querySelector('.daf-amount-error');
      this.variants = JSON.parse(this.querySelector('[data-amount-variants]').textContent);
      this.idInput = this.form?.querySelector('input[name="id"]');
      if (!this.idInput) return;
      // Preserve text/focus while the theme refreshes prices, quantity and buy buttons.
      const block = this.closest('[data-block-type]');
      if (block) block.dataset.blockType = 'giftcard-amount';
      this.appliedId = this.dataset.selected;
      const selected = JSON.parse(this.querySelector('[data-amount-selected]').textContent);
      const explicitVariant = new URL(location.href).searchParams.get('variant');
      this.input.value = selected && explicitVariant === String(selected.id) ? String(selected.price / 100) : '';
      const options = { signal: lifecycle.signal };
      this.input.addEventListener('input', () => this.update(), options);
      this.presets = Array.from(this.querySelectorAll('[data-amount-preset]'));
      this.presets.forEach(button => {
        button.addEventListener('click', () => {
          this.input.value = button.dataset.amountPreset;
          this.update();
        }, options);
      });
      this.form.addEventListener('submit', event => {
        const { variant } = this.resolve();
        if (this.pending || !variant || String(variant.id) !== this.appliedId || this.idInput.value !== String(variant.id) || this.idInput.disabled) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.input.reportValidity();
        }
      }, { ...options, capture: true });
      this.update();
      if (Number(this.dataset.variantCount) > this.variants.length) this.loadVariants(lifecycle);
    });
  }

  disconnectedCallback() {
    this.lifecycle?.abort();
    this.request?.abort();
    clearTimeout(this.timer);
  }

  resolve() { return resolveAmount(this.input.value, this.variants, this.dataset.currency); }

  async loadVariants(lifecycle) {
    this.loadingVariants = true;
    try {
      const variants = [];
      const url = new URL(location.href);
      url.searchParams.delete('variant');
      url.searchParams.delete('option_values');
      url.searchParams.set('section_id', 'giftcard-variants-data');
      for (let page = 1; page <= 10; page++) {
        url.searchParams.set('page', String(page));
        const response = await fetch(url, { signal: lifecycle.signal });
        if (!response.ok) throw new Error('Variant request failed');
        const fragment = document.createRange().createContextualFragment(await response.text());
        const data = JSON.parse(fragment.querySelector('[data-giftcard-page]').textContent);
        if (String(data.product) !== this.dataset.productId) throw new Error('Product mismatch');
        variants.push(...data.variants);
        if (page >= data.pages) break;
      }
      if (lifecycle.signal.aborted || !this.isConnected) return;
      if (new Set(variants.map(v => v.id)).size !== Number(this.dataset.variantCount)) throw new Error('Incomplete variants');
      this.variants = variants;
    } catch (error) {
      if (lifecycle.signal.aborted) return;
      this.variantsLoadFailed = true;
    } finally {
      if (!lifecycle.signal.aborted && this.isConnected) {
        this.loadingVariants = false;
        this.update();
      }
    }
  }

  syncPresets() {
    const selected = this.resolve().variant;
    this.presets.forEach(button => {
      const { variant } = resolveAmount(button.dataset.amountPreset, this.variants, this.dataset.currency);
      button.disabled = !variant;
      button.setAttribute('aria-pressed', String(Boolean(variant && selected && variant.id === selected.id)));
    });
  }

  lock(message, invalid = true) {
    this.closest('.product-info')?.querySelectorAll('[data-daf-amount-price]').forEach(el => { el.style.display = 'none'; });
    this.idInput.value = '';
    this.idInput.disabled = true;
    this.input.setCustomValidity(message);
    this.input.setAttribute('aria-invalid', String(invalid));
    this.error.textContent = message;
    Array.from(this.form.elements).filter(el => el.type === 'submit').forEach(el => { el.disabled = true; });
  }

  unlock(variant) {
    this.closest('.product-info')?.querySelectorAll('[data-daf-amount-price]').forEach(el => { el.style.display = ''; });
    this.syncPresets();
    this.pending = false;
    this.idInput.value = String(variant.id);
    this.idInput.disabled = false;
    this.input.setCustomValidity('');
    this.input.setAttribute('aria-invalid', 'false');
    this.error.textContent = '';
    Array.from(this.form.elements).filter(el => el.type === 'submit').forEach(el => { el.disabled = false; });
  }

  update() {
    this.syncPresets();
    clearTimeout(this.timer);
    this.request?.abort();
    // Invalidate even when the new value is invalid or is the last applied value.
    this.request = new AbortController();
    this.pending = false;
    if (!this.input.value) {
      this.lock('', false);
      this.input.setCustomValidity('Kies een bedrag of vul zelf een bedrag in.');
      return;
    }
    const { variant, error } = this.resolve();
    if (error && this.loadingVariants) return this.lock('Beschikbare bedragen worden geladen…', false);
    if (error && this.variantsLoadFailed) return this.lock('De bedragen konden niet worden geladen. Vernieuw de pagina.');
    if (error) return this.lock(error);
    if (String(variant.id) === this.appliedId) return this.unlock(variant);
    this.pending = true;
    this.lock('Bedrag wordt bijgewerkt…', false);
    const request = this.request;
    this.timer = setTimeout(() => this.select(variant, request), 200);
  }

  async select(variant, request) {
    const timeout = setTimeout(() => request.abort(), 12000);
    try {
      const url = new URL(location.href);
      url.searchParams.delete('option_values');
      url.searchParams.set('section_id', this.getAttribute('section-id'));
      url.searchParams.set('variant', variant.id);
      const response = await fetch(url, { signal: request.signal });
      if (!response.ok) throw new Error('Section request failed');
      const html = await response.text();
      if (request !== this.request || !this.isConnected) return;
      const fragment = document.createRange().createContextualFragment(html);
      const incoming = fragment.querySelector('daf-giftcard-amount');
      if (incoming?.dataset.selected !== String(variant.id)) throw new Error('Variant mismatch');
      const fresh = JSON.parse(incoming.querySelector('[data-amount-selected]').textContent);
      const checked = resolveAmount(this.input.value, fresh ? [fresh] : [], incoming.dataset.currency);
      if (!checked.variant || checked.variant.id !== variant.id) throw new Error(checked.error || 'Variant mismatch');
      const previousVariant = this.variants.find(item => String(item.id) === this.appliedId);
      this.variants = this.variants.map(item => item.id === fresh.id ? fresh : item);
      this.idInput.value = String(variant.id);
      this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      this.form.dispatchEvent(new CustomEvent('product:rerender', { detail: { htmlFragment: fragment, productChange: false } }));
      this.appliedId = String(variant.id);
      this.dataset.selected = this.appliedId;
      this.form.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { formId: this.form.getAttribute('id'), variant: checked.variant, previousVariant } }));
      const pageUrl = new URL(location.href);
      pageUrl.searchParams.set('variant', variant.id);
      history.replaceState({}, '', pageUrl);
      this.unlock(checked.variant);
    } catch (error) {
      if (request !== this.request || !this.isConnected) return;
      this.pending = false;
      this.lock('Het bedrag kon niet worden bijgewerkt. Vul het opnieuw in of vernieuw de pagina.');
    } finally {
      clearTimeout(timeout);
    }
  }
}

if (!customElements.get('daf-giftcard-amount')) customElements.define('daf-giftcard-amount', GiftcardAmount);
